import React, { useMemo, useState, useCallback } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { useGetCampaignsQuery, useGetDashboardStatsQuery, useGetTimeSeriesStatsQuery, Campaign } from '../store/services/whatsappApi';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { format, subDays, isAfter } from 'date-fns';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Analytics: React.FC = () => {
  const [timeframe, setTimeframe] = useState<'day' | 'week' | 'month'>('week');
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);

  // Fetch limited, ordered campaigns to reduce payload
  const { data: campaigns, isLoading, error } = useGetCampaignsQuery({ orderBy: 'created_at', orderDir: 'desc', limit: 200, skip: 0 });
  // Fetch aggregated stats for KPI cards
  const { data: dashboardStats } = useGetDashboardStatsQuery();
  // Fetch time-series data
  const { data: timeSeriesData } = useGetTimeSeriesStatsQuery({ days: timeframe === 'day' ? 1 : timeframe === 'week' ? 7 : 30 });
  
  // Get date range based on selected timeframe
  const getDateRange = useCallback(() => {
    const today = new Date();
    let days = 7; // default week
    
    if (timeframe === 'day') {
      days = 1;
    } else if (timeframe === 'month') {
      days = 30;
    }
    
    return Array.from({ length: days }, (_, i) => {
      const date = subDays(today, days - i - 1);
      return format(date, 'MMM dd');
    });
  }, [timeframe]);
  
  // Filter data based on timeframe
  const filteredCampaigns = useMemo<Campaign[]>(() => {
    const source: Campaign[] = campaigns ?? [];
    const startDate = subDays(new Date(), timeframe === 'day' ? 1 : timeframe === 'week' ? 7 : 30);
    return source.filter(campaign => !!campaign.created_at && isAfter(new Date(campaign.created_at), startDate));
  }, [campaigns, timeframe]);

  const dateRange = useMemo(() => getDateRange(), [getDateRange]);
  
  // Generate data for message status chart
  const messageStatusData = useMemo(() => {
    const totals = filteredCampaigns.reduce(
      (acc, c) => [
        acc[0] + (c.stats?.sent || 0),
        acc[1] + (c.stats?.delivered || 0),
        acc[2] + (c.stats?.read || 0),
        acc[3] + (c.stats?.failed || 0)
      ],
      [0, 0, 0, 0]
    );
    return {
      labels: ['Sent', 'Delivered', 'Read', 'Failed'],
      datasets: [
        {
          label: 'Message Status',
          data: totals,
          backgroundColor: [
            'rgba(54, 162, 235, 0.6)',
            'rgba(75, 192, 192, 0.6)',
            'rgba(40, 167, 69, 0.6)',
            'rgba(255, 99, 132, 0.6)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(40, 167, 69, 1)',
            'rgba(255, 99, 132, 1)'
          ],
          borderWidth: 1
        }
      ]
    };
  }, [filteredCampaigns]);
  
  // Generate data for campaign performance over time
  const campaignPerformanceData = useMemo(() => {
    if (!timeSeriesData) {
      return {
        labels: dateRange,
        datasets: [
          {
            label: 'Sent Messages',
            data: dateRange.map(() => 0),
            borderColor: 'rgba(54, 162, 235, 1)',
            backgroundColor: 'rgba(54, 162, 235, 0.2)'
          },
          {
            label: 'Delivered Messages',
            data: dateRange.map(() => 0),
            borderColor: 'rgba(75, 192, 192, 1)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)'
          },
          {
            label: 'Read Messages',
            data: dateRange.map(() => 0),
            borderColor: 'rgba(40, 167, 69, 1)',
            backgroundColor: 'rgba(40, 167, 69, 0.2)'
          }
        ]
      };
    }

    return {
      labels: dateRange,
      datasets: [
        {
          label: 'Sent Messages',
          data: dateRange.map(date => {
            const dateStr = format(new Date(date), 'yyyy-MM-dd');
            return timeSeriesData[dateStr]?.sent || 0;
          }),
          borderColor: 'rgba(54, 162, 235, 1)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)'
        },
        {
          label: 'Delivered Messages',
          data: dateRange.map(date => {
            const dateStr = format(new Date(date), 'yyyy-MM-dd');
            return timeSeriesData[dateStr]?.delivered || 0;
          }),
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)'
        },
        {
          label: 'Read Messages',
          data: dateRange.map(date => {
            const dateStr = format(new Date(date), 'yyyy-MM-dd');
            return timeSeriesData[dateStr]?.read || 0;
          }),
          borderColor: 'rgba(40, 167, 69, 1)',
          backgroundColor: 'rgba(40, 167, 69, 0.2)'
        }
      ]
    };
  }, [dateRange, timeSeriesData]);
  
  // Generate data for campaign performance comparison
  const campaignComparisonData = useMemo(() => ({
    labels: filteredCampaigns.slice(0, 5).map(c => c.name),
    datasets: [
      {
        label: 'Delivered Rate (%)',
        data: filteredCampaigns.slice(0, 5).map(c => (c.stats?.sent || 0) > 0 ? Math.round(((c.stats?.delivered || 0) / (c.stats?.sent || 0)) * 100) : 0),
        backgroundColor: 'rgba(75, 192, 192, 0.6)'
      },
      {
        label: 'Read Rate (%)',
        data: filteredCampaigns.slice(0, 5).map(c => (c.stats?.sent || 0) > 0 ? Math.round(((c.stats?.read || 0) / (c.stats?.sent || 0)) * 100) : 0),
        backgroundColor: 'rgba(40, 167, 69, 0.6)'
      }
    ]
  }), [filteredCampaigns]);
  
  // Selected campaign details
  const selectedCampaign = selectedCampaignId 
    ? campaigns?.find(c => c.id === selectedCampaignId) 
    : null;
  
  if (isLoading) {
    return (
      <MainLayout>
        <div className="p-4">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Analytics</h1>
          </div>
          <div className="animate-pulse">
            <div className="bg-gray-200 h-64 mb-6 rounded-lg"></div>
            <div className="bg-gray-200 h-64 mb-6 rounded-lg"></div>
            <div className="bg-gray-200 h-64 rounded-lg"></div>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  if (error) {
    return (
      <MainLayout>
        <div className="p-4">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Analytics</h1>
          </div>
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
            <p className="font-bold">Error</p>
            <p>Failed to load analytics data. Please try again later.</p>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout>
      <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Analytics</h1>
        <div className="flex space-x-2">
          <button 
            className={`px-4 py-2 rounded-md ${timeframe === 'day' ? 'bg-whatsapp-green text-white' : 'bg-gray-200'}`}
            onClick={() => setTimeframe('day')}
          >
            Today
          </button>
          <button 
            className={`px-4 py-2 rounded-md ${timeframe === 'week' ? 'bg-whatsapp-green text-white' : 'bg-gray-200'}`}
            onClick={() => setTimeframe('week')}
          >
            This Week
          </button>
          <button 
            className={`px-4 py-2 rounded-md ${timeframe === 'month' ? 'bg-whatsapp-green text-white' : 'bg-gray-200'}`}
            onClick={() => setTimeframe('month')}
          >
            This Month
          </button>
        </div>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <p className="text-sm text-gray-500">Messages Sent</p>
          <p className="text-2xl font-semibold">{dashboardStats?.message_stats?.sent ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <p className="text-sm text-gray-500">Delivered</p>
          <p className="text-2xl font-semibold">{dashboardStats?.message_stats?.delivered ?? 0}</p>
          <p className="text-xs text-gray-400">
            {dashboardStats?.message_stats?.sent ? Math.round((dashboardStats.message_stats.delivered / dashboardStats.message_stats.sent) * 100) : 0}% rate
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <p className="text-sm text-gray-500">Read</p>
          <p className="text-2xl font-semibold">{dashboardStats?.message_stats?.read ?? 0}</p>
          <p className="text-xs text-gray-400">
            {dashboardStats?.message_stats?.sent ? Math.round((dashboardStats.message_stats.read / dashboardStats.message_stats.sent) * 100) : 0}% rate
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <p className="text-sm text-gray-500">Failed</p>
          <p className="text-2xl font-semibold">{dashboardStats?.message_stats?.failed ?? 0}</p>
          <p className="text-xs text-gray-400">
            {dashboardStats?.message_stats?.sent ? Math.round((dashboardStats?.message_stats?.failed || 0) / dashboardStats.message_stats.sent * 100) : 0}% rate
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <p className="text-sm text-gray-500">Total Contacts</p>
          <p className="text-2xl font-semibold">{dashboardStats?.total_contacts ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <p className="text-sm text-gray-500">Active Campaigns</p>
          <p className="text-2xl font-semibold">{dashboardStats?.active_campaigns ?? 0}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Message Status Distribution */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <h2 className="text-lg font-semibold mb-4">Message Status Distribution</h2>
          <div className="h-80">
            <Doughnut 
              data={messageStatusData} 
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                  },
                },
              }} 
            />
          </div>
        </div>
        
        {/* Campaign Performance Over Time */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <h2 className="text-lg font-semibold mb-4">Message Performance Over Time</h2>
          <div className="h-80">
            <Line 
              data={campaignPerformanceData} 
              options={{
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                  },
                },
              }} 
            />
          </div>
        </div>
      </div>
      
      {/* Campaign Comparison */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <h2 className="text-lg font-semibold mb-4">Campaign Performance Comparison</h2>
        <div className="h-80">
          <Bar
            data={campaignComparisonData}
            options={{
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  max: 100,
                  title: {
                    display: true,
                    text: 'Percentage (%)',
                  },
                },
              },
            }}
          />
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <h2 className="text-lg font-semibold mb-4">Engagement Overview</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Overall Delivery Rate</span>
              <span className="font-medium">
                {dashboardStats?.message_stats?.sent ?
                  Math.round((dashboardStats.message_stats.delivered / dashboardStats.message_stats.sent) * 100) : 0}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Overall Read Rate</span>
              <span className="font-medium">
                {dashboardStats?.message_stats?.delivered ?
                  Math.round((dashboardStats.message_stats.read / dashboardStats.message_stats.delivered) * 100) : 0}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Failure Rate</span>
              <span className="font-medium text-red-600">
                {(() => {
                  const total = (dashboardStats?.message_stats?.sent || 0) + (dashboardStats?.message_stats?.failed || 0);
                  return total ? Math.round((dashboardStats?.message_stats?.failed || 0) / total * 100) : 0;
                })()}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg Messages per Contact</span>
              <span className="font-medium">
                {dashboardStats?.total_contacts ?
                  Math.round((dashboardStats.total_messages / dashboardStats.total_contacts) * 10) / 10 : 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <h2 className="text-lg font-semibold mb-4">Campaign Success Metrics</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Best Performing Campaign</span>
              <span className="font-medium">
                {filteredCampaigns.length > 0 ?
                  filteredCampaigns.reduce((best, current) =>
                    ((current.stats?.read || 0) / Math.max(current.stats?.delivered || 1, 1)) >
                    ((best.stats?.read || 0) / Math.max(best.stats?.delivered || 1, 1)) ? current : best
                  ).name : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Average Read Rate</span>
              <span className="font-medium">
                {filteredCampaigns.length > 0 ?
                  Math.round(filteredCampaigns.reduce((sum, c) =>
                    sum + ((c.stats?.delivered || 0) > 0 ? (c.stats?.read || 0) / (c.stats?.delivered || 0) : 0), 0
                  ) / filteredCampaigns.length * 100) : 0}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Campaign Reach</span>
              <span className="font-medium">
                {filteredCampaigns.reduce((sum, c) => sum + (c.stats?.sent || 0), 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Campaigns This Period</span>
              <span className="font-medium">{filteredCampaigns.length}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Campaign List */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h2 className="text-lg font-semibold mb-4">Campaign Details</h2>
        {filteredCampaigns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivered</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Read</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCampaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{campaign.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                        ${campaign.status === 'completed' ? 'bg-green-100 text-green-800' : 
                          campaign.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          campaign.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-gray-100 text-gray-800'}`}>
                        {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1).replace('_', ' ')}
                      </span>
                    </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{campaign.stats?.sent || 0}</td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                       {campaign.stats?.delivered || 0}
                       ({(campaign.stats?.sent || 0) > 0 ? Math.round(((campaign.stats?.delivered || 0) / (campaign.stats?.sent || 0)) * 100) : 0}%)
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                       {campaign.stats?.read || 0}
                       ({(campaign.stats?.sent || 0) > 0 ? Math.round(((campaign.stats?.read || 0) / (campaign.stats?.sent || 0)) * 100) : 0}%)
                     </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        className="text-whatsapp-green hover:text-whatsapp-dark-green"
                        onClick={() => setSelectedCampaignId(campaign.id)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            No campaigns found for the selected time period.
          </div>
        )}
      </div>
      
      {/* Campaign Detail Modal */}
      {selectedCampaign && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-2xl w-full">
            <div className="flex justify-between items-start">
              <h2 className="text-xl font-bold text-gray-800">{selectedCampaign.name}</h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setSelectedCampaignId(null)}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mt-4 space-y-4">
              <div>
                <p className="text-sm text-gray-500">Campaign Status</p>
                <p className="font-medium">{selectedCampaign.status.charAt(0).toUpperCase() + selectedCampaign.status.slice(1).replace('_', ' ')}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Template Used</p>
                <p className="font-medium">{selectedCampaign.template?.name || 'N/A'}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Schedule Information</p>
                <p className="font-medium">
                  {selectedCampaign.scheduled_at ? format(new Date(selectedCampaign.scheduled_at), 'PPpp') : 'Not scheduled'}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Campaign Performance</p>
                <div className="mt-2 space-y-2">
                   <div className="flex justify-between items-center">
                     <span className="text-sm">Sent</span>
                     <span className="font-medium">{selectedCampaign.stats?.sent || 0}</span>
                   </div>
                   <div className="flex justify-between items-center">
                     <span className="text-sm">Delivered</span>
                     <span className="font-medium">
                       {selectedCampaign.stats?.delivered || 0}
                       ({(selectedCampaign.stats?.sent || 0) > 0 ? Math.round(((selectedCampaign.stats?.delivered || 0) / (selectedCampaign.stats?.sent || 0)) * 100) : 0}%)
                     </span>
                   </div>
                   <div className="flex justify-between items-center">
                     <span className="text-sm">Read</span>
                     <span className="font-medium">
                       {selectedCampaign.stats?.read || 0}
                       ({(selectedCampaign.stats?.sent || 0) > 0 ? Math.round(((selectedCampaign.stats?.read || 0) / (selectedCampaign.stats?.sent || 0)) * 100) : 0}%)
                     </span>
                   </div>
                   <div className="flex justify-between items-center">
                     <span className="text-sm">Failed</span>
                     <span className="font-medium">
                       {selectedCampaign.stats?.failed || 0}
                       ({(selectedCampaign.stats?.sent || 0) > 0 ? Math.round(((selectedCampaign.stats?.failed || 0) / (selectedCampaign.stats?.sent || 0)) * 100) : 0}%)
                     </span>
                   </div>
                </div>
              </div>
              
              <div className="pt-4 flex justify-end">
                <button
                  className="bg-whatsapp-green text-white px-4 py-2 rounded-md hover:bg-whatsapp-dark-green"
                  onClick={() => setSelectedCampaignId(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </MainLayout>
  );
};

export default Analytics;
